import {
  DatabaseOutlined,
  HeartOutlined,
  NodeIndexOutlined,
  SettingOutlined,
  AppstoreOutlined,
  LoadingOutlined,
  CloudDownloadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import {
  Button,
  Checkbox,
  Collapse,
  Input,
  Popover,
  Table,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import { useGetLocationsQuery } from 'gql/types';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { GET_SHAREPOINT_LIST_ITEMS_QUERY } from '../../graphql/queries';
import styles from '../OperationsDashboard/ops-theme.module.css';

const COMPANY_DETAILS_LIST_ID = '98c6675e-a7b7-4945-aedb-1204dee31251';

const { Panel } = Collapse;

/* ------------------------------------------------------------------ */
/*  Column definitions per list — from SharePoint Graph API discovery  */
/* ------------------------------------------------------------------ */

type Col = { name: string; display: string };

/** Convert a cell value to a display string */
const cellToString = (val: unknown): string => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
};

/** Search filter dropdown rendered inside each column header */
const SearchFilterDropdown: FC<FilterDropdownProps> = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
}) => (
  <div style={{ padding: 8 }}>
    <Input
      placeholder="Search..."
      value={selectedKeys[0]}
      onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
      onPressEnter={() => confirm()}
      style={{ width: 180, marginBottom: 8, display: 'block' }}
      size="small"
    />
    <Button
      type="primary"
      onClick={() => confirm()}
      size="small"
      style={{ width: 80, marginRight: 8 }}
    >
      Filter
    </Button>
    <Button
      onClick={() => {
        clearFilters?.();
        confirm();
      }}
      size="small"
      style={{ width: 80 }}
    >
      Reset
    </Button>
  </div>
);

/**
 * Build antd columns with sorting & text search filtering.
 * For columns with <=20 unique values, use checkbox filters instead of text search.
 */
const buildAntCols = (
  cols: Col[],
  data: Record<string, unknown>[]
): ColumnsType<Record<string, unknown>> =>
  cols.map((c) => {
    // Collect unique non-empty values for this column
    const uniqueVals = new Set<string>();
    for (const row of data) {
      const s = cellToString(row[c.name]);
      if (s) uniqueVals.add(s);
    }

    const useCheckboxFilter = uniqueVals.size > 0 && uniqueVals.size <= 20;

    const isDropsCol = c.name.startsWith('_drops');

    const col: ColumnsType<Record<string, unknown>>[number] = {
      title: c.display,
      dataIndex: c.name,
      key: c.name,
      ellipsis: true,
      className: isDropsCol ? styles.pawsDropsCol : undefined,
      sorter: (a, b) => {
        const aVal = cellToString(a[c.name]);
        const bVal = cellToString(b[c.name]);
        // Try numeric sort first
        const aNum = Number(aVal);
        const bNum = Number(bVal);
        if (!isNaN(aNum) && !isNaN(bNum) && aVal !== '' && bVal !== '') {
          return aNum - bNum;
        }
        return aVal.localeCompare(bVal);
      },
      render: (val: unknown) => {
        const s = cellToString(val);
        return s || '-';
      },
    };

    if (useCheckboxFilter) {
      // Checkbox filter for low-cardinality columns
      col.filters = [...uniqueVals].sort().map((v) => ({ text: v, value: v }));
      col.onFilter = (value, record) => cellToString(record[c.name]) === value;
    } else if (uniqueVals.size > 20) {
      // Text search for high-cardinality columns
      col.filterDropdown = (props) => <SearchFilterDropdown {...props} />;
      col.onFilter = (value, record) =>
        cellToString(record[c.name])
          .toLowerCase()
          .includes(String(value).toLowerCase());
    }

    return col;
  });

/** Columns per list ID (system cols like Modified/Created/Author/Editor/_IsRecord stripped) */
const LIST_COLUMNS: Record<string, Col[]> = {
  // ── CRM / Core Data ──
  'd2d77a5b-fd33-4212-a21b-15f8a95cf872': [
    // CRM
    { name: 'Title', display: 'Title' },
    { name: 'DateIdentified', display: 'Date Identified' },
    { name: 'CompanyName', display: 'Company Name' },
    { name: 'NAVBBTeamMember', display: 'NAVBB Marketing Member' },
    { name: 'CompanyType', display: 'Company Type' },
    { name: 'CompanyContact', display: 'Company Contact' },
    { name: 'EstimatedDonorCount', display: 'Estimated Donor Count' },
    { name: 'FirstContact', display: 'First Contact' },
    { name: 'LastContact', display: 'Last Contact' },
    { name: 'NextAction', display: 'Next Action' },
    { name: 'NextContact', display: 'Next Contact' },
    { name: 'PipelineStage', display: 'Pipeline Stage' },
    { name: 'RegisteredDonorCount', display: 'Registered Donor Count' },
    { name: 'Pending', display: 'Pending' },
    { name: 'Active', display: 'Active' },
    { name: 'Retired', display: 'Retired' },
    { name: 'SiteVisit1', display: 'Intake' },
    {
      name: 'DonorRegistrationEventDate',
      display: 'Donor Registration Event Date',
    },
    {
      name: 'DonorRegistrationEventDate2',
      display: 'Donor Registration Event Date 2',
    },
    { name: 'BloodDrawDate', display: 'Blood Draw Date' },
    { name: 'Notes', display: 'Notes' },
    { name: 'Affiliate', display: 'Affiliate' },
    { name: 'NotAccepted', display: 'Not Accepted' },
    { name: 'RecruitingEvent1', display: 'Recruiting Event 1' },
    { name: 'RecruitingEvent2', display: 'Recruiting Event 2' },
    {
      name: 'AdditionalRecruitingEvents',
      display: 'Additional Recruiting Events',
    },
    { name: 'ScreeningDate', display: 'Screening Day' },
    { name: 'FirstDonationDay', display: 'First Donation Day' },
    { name: '_x0023_Employees', display: '# of Employees at Company' },
    { name: 'ContactPerson', display: 'ContactPerson' },
    { name: 'Region', display: 'Region' },
    { name: 'Company_x0020_Name_x003a__x0020_', display: 'Company Name: ID' },
  ],
  '98c6675e-a7b7-4945-aedb-1204dee31251': [
    // Company Details
    { name: 'Title', display: 'Daycare' },
    { name: 'Website', display: 'Website' },
    { name: 'CompanyType', display: 'Company Type' },
    { name: 'Logo', display: 'Logo' },
    { name: 'City', display: 'City' },
    { name: 'State', display: 'State' },
    { name: 'Address', display: 'Address' },
    { name: 'ZIP', display: 'ZIP' },
    { name: 'CompanyPhone', display: 'Phone' },
    { name: 'Status', display: 'Status' },
    { name: 'PartnerDate', display: 'Partner Date' },
    { name: 'NoOfLargeDogs', display: '# of large dogs' },
    { name: 'Region', display: 'Region' },
    { name: 'DayOfWeekPreferences', display: 'Day of Week Preferences' },
    { name: 'AcceptsPickupAppts', display: 'Accepts pickup appts?' },
    { name: 'AcceptsExternalClients', display: 'Accepts external clients?' },
    { name: 'UniqueCode', display: 'Unique Code' },
    { name: 'LocationType', display: 'Location Type' },
    { name: 'Address2', display: 'Address2' },
    { name: 'PartnerType', display: 'Partner Type' },
  ],
  '98327160-1c2f-42ad-bc9b-668f85ff108a': [
    // Contact Details
    { name: 'Title', display: 'Title' },
    { name: 'Name', display: 'Contact Name' },
    { name: 'WorkFunction', display: 'Work Function' },
    { name: 'WorkPhone', display: 'Work Phone' },
    { name: 'WorkEmail', display: 'Work Email' },
    { name: 'MobilePhone', display: 'Mobile Phone' },
    { name: 'PersonalEmail', display: 'Personal Email' },
    { name: 'Notes', display: 'Notes' },
    { name: 'Company', display: 'Company' },
    { name: 'Category', display: 'Category' },
    { name: 'PreferredCOntactMethod', display: 'Preferred Contact Method' },
    { name: 'MainContact', display: 'MainContact' },
  ],
  '81cccec5-243f-41cb-92cd-4f703dd5a90a': [
    // Contact Log
    { name: 'Title', display: 'Title' },
    { name: 'ContactDate', display: 'Contact Date' },
    { name: 'Organization', display: 'Organization' },
    { name: 'TeamMember', display: 'Team Member' },
    { name: 'Notes', display: 'Notes' },
    { name: 'ContactMethod0', display: 'Contact Method' },
    { name: 'Purpose', display: 'Purpose' },
    {
      name: 'Organization_x003a__x0020_Daycar',
      display: 'Organization: Daycare',
    },
  ],
  '1bfc2af2-add7-48b1-9846-1fe4afbaba19': [
    // CRM_Notes - Partnership
    { name: 'Title', display: 'Title' },
    { name: 'CRMCompany', display: 'CRMCompany' },
    { name: 'Comment', display: 'Note' },
    { name: 'Pinned', display: 'Pinned' },
  ],
  '4c97e8ae-c71a-423b-9b9a-a5e694975d4e': [
    // CRM_Notes - Mkt Details
    { name: 'Title', display: 'Title' },
    { name: 'CRMCompany', display: 'CRMCompany' },
    { name: 'Comment', display: 'Notes' },
    { name: 'Pinned', display: 'Pinned' },
  ],

  // ── Donor / Blood Program ──
  '05ee6196-8aed-4b99-95b5-374a227a4e45': [
    // CSV-Donors
    { name: 'Title', display: 'Title' },
    { name: 'name', display: 'Name' },
    { name: 'lastName', display: 'Last Name' },
    { name: 'ownerId', display: 'Owner ID' },
    { name: 'location', display: 'Location' },
    { name: 'age', display: 'Age' },
    { name: 'weight', display: 'Weight (lbs)' },
    { name: 'primaryVeterinarian', display: 'Primary Veterinarian' },
    { name: 'species', display: 'Species' },
    { name: 'gender', display: 'Gender' },
    { name: 'bloodType', display: 'Blood Type' },
    { name: 'status', display: 'Status' },
    { name: 'breed', display: 'Breed' },
    { name: 'color', display: 'Color' },
    { name: 'shortId', display: 'Short ID' },
    { name: 'becameReservedDonorDate', display: 'Became Reserved Donor Date' },
    { name: 'rabiesDate', display: 'Rabies Date' },
    { name: 'distemperDate', display: 'Distemper Rate' },
    { name: 'retiredDate', display: 'Retired Date' },
    { name: 'nextDonationDate', display: 'Next Donation Date' },
    { name: 'totalCollected', display: 'Total Collected (mL)' },
    { name: 'Neutered', display: 'Neutered?' },
    { name: 'RegDate', display: 'RegDate' },
    { name: 'ScreenedDate', display: 'ScreenedDate' },
    { name: 'ScreeningAttended', display: 'ScreeningAttended' },
    { name: 'ScreeningNoShow', display: 'ScreeningNoShow' },
    { name: 'DonationNoShow', display: 'DonationNoShow' },
    { name: 'microchipNumber', display: 'microchipNumber' },
    { name: 'approvedDonationML', display: 'approvedDonationML' },
    { name: 'donationMLApprovedAmount', display: 'donationMLApprovedAmount' },
    { name: 'donationFrequency', display: 'donationFrequency' },
    { name: 'acceptSimparicaTrio', display: 'acceptSimparicaTrio' },
    { name: 'LabworkDue', display: 'LabworkDue' },
    { name: 'Notes', display: 'Notes' },
    { name: 'Region', display: 'Region' },
    { name: 'AnnualDVMComplete', display: 'AnnualDVMComplete' },
    { name: 'RequiresAppointment', display: 'RequiresAppointment' },
  ],
  '008b1454-a5a4-4fff-837a-4c330310d799': [
    // CSV - Donations
    { name: 'Title', display: 'Title' },
    { name: 'shortId', display: 'Short ID' },
    { name: 'temperature', display: 'Temperature (°F)' },
    { name: 'pulse', display: 'Pulse (bpm)' },
    { name: 'respiration', display: 'Respiration' },
    { name: 'capillaryRefillTime', display: 'Capillary Refill Time' },
    { name: 'mucousMembrane', display: 'Mucous Membrane' },
    { name: 'packedCellVolume', display: 'Packed Cell Volume (mL)' },
    { name: 'totalSolids', display: 'Total Solids' },
    { name: 'weight', display: 'Weight (kg)' },
    { name: 'working', display: 'Working' },
    { name: 'unit', display: 'Unit' },
    { name: 'actual', display: 'Actual' },
    { name: 'donationDate', display: 'Donation Date' },
    { name: 'additionalInfoId', display: 'Additional Info ID' },
    {
      name: 'numberOfCollectionUnitsUsed',
      display: 'Number of Collection Units Used',
    },
    { name: 'primaryHolderFirstName', display: 'Primary Holder First Name' },
    { name: 'primaryHolderLastName', display: 'Primary Holder Last Name' },
    { name: 'pokerFirstName', display: 'Poker First Name' },
    { name: 'pokerLastName', display: 'Poker Last Name' },
    { name: 'donorId', display: 'Donor Id' },
    { name: 'donorFirstName', display: 'Donor First Name' },
    { name: 'donorLastName', display: 'Donor Last Name' },
    { name: 'donorLocation', display: 'Donor Location' },
    { name: 'IDtxt', display: 'IDtxt' },
    { name: 'ApprovedDonationML', display: 'ApprovedDonationML' },
    { name: 'jugularVein', display: 'jugularVein' },
    { name: 'IneligibleReason', display: 'IneligibleReason' },
    { name: 'Comment', display: 'Comment' },
    { name: 'donationNumber', display: 'donationNumber' },
    { name: 'donorRegion', display: 'donorRegion' },
  ],
  '4f9291fc-6011-4fab-81ec-5a596ba98b6b': [
    // CSV-Owners
    { name: 'Title', display: 'Original ID' },
    { name: 'shortId', display: 'Short ID' },
    { name: 'email', display: 'E-mail' },
    { name: 'firstName', display: 'First Name' },
    { name: 'lastName', display: 'Last Name' },
    { name: 'phoneNumber', display: 'Phone Number' },
    { name: 'streetAddress', display: 'Street Address' },
    { name: 'city', display: 'City' },
    { name: 'state', display: 'State' },
    { name: 'zipCode', display: 'ZIP Code' },
    { name: 'locationPreferences', display: 'Location Preferences' },
    { name: 'birthday', display: 'Birthday' },
    { name: 'type', display: 'Type' },
    { name: 'isArchived', display: 'Is Archived?' },
    { name: 'donors', display: 'Donors' },
    { name: 'contactPreferences', display: 'contactPreferences' },
    { name: 'regionPreferences', display: 'regionPreferences' },
  ],
  '2f3ff74e-be28-4fc6-802b-3880c1473915': [
    // Donor Status
    { name: 'Title', display: 'Title' },
    { name: 'MainCategory', display: 'MainCategory' },
  ],
  '68b014e9-d79c-47c6-9211-223fee40570a': [
    // Conversions per Quarter - Donors
    { name: 'Title', display: 'Title' },
    { name: 'Donor', display: 'Donor' },
    { name: 'BecameActiveDate', display: 'Became Active Date' },
    { name: 'CurrentStatus', display: 'CurrentStatus' },
  ],

  // ── Pipeline / Workflow ──
  '02e1f670-8a34-4356-b794-492237dee2d3': [
    // Pipeline Stage
    { name: 'Title', display: 'Title' },
    { name: 'HEXColor', display: 'HEXColor' },
    { name: 'Description', display: 'Description' },
  ],
  '24cd7052-94f5-4360-9f6d-03a3ad78b705': [
    // Pipeline-StageTracker
    { name: 'Title', display: 'Title' },
    { name: 'AwarenessStartDate', display: 'Awareness Start Date' },
    { name: 'AwarenessEndDate', display: 'Awareness End Date' },
    { name: 'AwarenessDuration', display: 'Awareness Duration' },
    { name: 'ConsiderationStartDate', display: 'Consideration Start Date' },
    { name: 'ConsiderationEndDate', display: 'Consideration End Date' },
    { name: 'ConsiderationDuration', display: 'Consideration Duration' },
    { name: 'SupportStartDate', display: 'Support Start Date' },
    { name: 'SupportEndDate', display: 'Support End Date' },
    { name: 'SupportDuration', display: 'Support Duration' },
    { name: 'PartnerStartDate', display: 'Partner Start Date' },
    { name: 'PartnerEndDate', display: 'Partner End Date' },
    { name: 'PartnerDuration', display: 'Partner Duration' },
    { name: 'Daycare', display: 'Selected Daycare' },
    { name: 'CurrentStage', display: 'Current Stage' },
  ],

  // ── System / Integration ──
  '3b6a018f-03e1-4c79-a993-306f4d45dc2b': [
    // DROPSUploadLog
    { name: 'Title', display: 'Title' },
    { name: 'Category', display: 'Category' },
    { name: 'NoOfItems', display: '# of items' },
    { name: 'ExecutionTime', display: 'Execution Time' },
  ],
  'c4c1f37d-2923-4b1f-9f88-2f20624aca86': [
    // UpdateDatabase-Trigger
    { name: 'Title', display: 'Title' },
  ],

  // ── Other ──
  '7aec5b76-68a1-4199-9f2e-00c3e140c146': [
    // Goals
    { name: 'Title', display: 'Title' },
    { name: 'Year', display: 'Year' },
    { name: 'Month', display: 'Month' },
    { name: 'Quarter', display: 'Quarter' },
    { name: 'Goal', display: 'Goal' },
    { name: 'GoalType', display: 'Goal Period' },
  ],
  'db222e92-d6c5-4a6e-aeee-13e71491e27a': [
    // Partner Event Log
    { name: 'Title', display: 'Title' },
    { name: 'TeamMember', display: 'Team Member' },
    { name: 'Notes', display: 'Notes' },
    { name: 'Daycare', display: 'Daycare' },
    { name: 'EventDate', display: 'Event Date' },
    { name: 'EventType', display: 'Event Type' },
    { name: 'DonorsRegistered', display: 'Donors Registered' },
    { name: 'Daycare_x003a__x0020_Daycare', display: 'Daycare: Daycare' },
  ],
  '99f75c03-732f-4fc1-be8a-d95143a6d45b': [
    // Team
    { name: 'Title', display: 'Title' },
    { name: 'FullName', display: 'Team Member' },
    { name: 'Role', display: 'Role' },
    { name: 'LookupName', display: 'Lookup Name' },
    { name: 'PermissionType', display: 'Permission Type' },
    { name: 'SecurityGroup', display: 'Security Group' },
  ],
  '5f79ec0d-7ba5-489c-aa8b-bf7cf5530273': [
    // Offices Location
    { name: 'Title', display: 'Title' },
    { name: 'Region', display: 'Region' },
    { name: 'State', display: 'State' },
    { name: 'City', display: 'City' },
    { name: 'Address', display: 'Address' },
    { name: 'ZIP', display: 'ZIP' },
    { name: 'Phone', display: 'Phone' },
  ],
  '3b90d2db-a5dc-4d3e-9ac5-74716eccf925': [
    // Mobile-Notes
    { name: 'Title', display: 'Title' },
    { name: 'Daycare', display: 'Daycare' },
    { name: 'Daycare_x003a__x0020_Daycare', display: 'Daycare: Daycare' },
    { name: 'Notes', display: 'Notes' },
  ],
  'e11b5ea0-47ea-4103-a07d-56a025255802': [
    // NextContactCalendar
    { name: 'Title', display: 'Title' },
    { name: 'DaycaresRecord', display: 'DaycaresRecord' },
    { name: 'NextContactDate', display: 'NextContactDate' },
    { name: 'Responsible', display: 'Responsible' },
  ],
  '201db96c-7954-49c2-9c7a-8754e9f05e49': [
    // Support-Feedback
    { name: 'Title', display: 'Ticket Subject' },
    { name: 'RequestType', display: 'RequestType' },
    { name: 'Risk', display: 'Risk' },
    { name: 'Description', display: 'Description' },
    { name: 'Status', display: 'Status' },
    { name: 'Manager', display: 'Manager' },
    { name: 'ApprovedDeniedBy', display: 'Approved/Denied By' },
    { name: 'ApprovedDenied', display: 'Approved/Denied' },
    { name: 'Comments', display: 'Comments' },
    { name: 'InProgressStart', display: 'In Progress Start Date' },
    { name: 'InProgressEnd', display: 'In Progress End Date' },
    { name: 'NotifyUser', display: 'NotifyUser' },
    { name: 'NotResolvedComments', display: 'Not Resolved Reason' },
  ],
  'e4d94136-1b61-4c06-827a-1d47e3cab683': [
    // SecurityGroups
    { name: 'Title', display: 'Title' },
    { name: 'GroupId', display: 'GroupId' },
  ],
  'e20fc58f-46b5-4dcf-bb8b-65a58416fafd': [
    // Security
    { name: 'Title', display: 'Title' },
  ],
};

/* ------------------------------------------------------------------ */
/*  Section definitions                                                */
/* ------------------------------------------------------------------ */

interface IPawsList {
  displayName: string;
  internalName: string;
  listId: string;
}

interface IPawsSection {
  title: string;
  icon: React.ReactNode;
  lists: IPawsList[];
}

const SECTIONS: IPawsSection[] = [
  {
    title: 'CRM / Core Data Lists',
    icon: <DatabaseOutlined />,
    lists: [
      {
        displayName: 'CRM',
        internalName: 'CRM',
        listId: 'd2d77a5b-fd33-4212-a21b-15f8a95cf872',
      },
      {
        displayName: 'Company Details',
        internalName: 'CompanyDetails',
        listId: '98c6675e-a7b7-4945-aedb-1204dee31251',
      },
      {
        displayName: 'Contact Details',
        internalName: 'ContactDetails',
        listId: '98327160-1c2f-42ad-bc9b-668f85ff108a',
      },
      {
        displayName: 'Contact Log',
        internalName: 'ContactLog',
        listId: '81cccec5-243f-41cb-92cd-4f703dd5a90a',
      },
      {
        displayName: 'CRM_Notes - Partnership',
        internalName: 'CRM_Notes  Partnership',
        listId: '1bfc2af2-add7-48b1-9846-1fe4afbaba19',
      },
      {
        displayName: 'CRM_Notes - Mkt Details',
        internalName: 'CRM_Notes',
        listId: '4c97e8ae-c71a-423b-9b9a-a5e694975d4e',
      },
    ],
  },
  {
    title: 'Donor / Blood Program Data Lists',
    icon: <HeartOutlined />,
    lists: [
      {
        displayName: 'CSV-Donors',
        internalName: 'CSVDonors',
        listId: '05ee6196-8aed-4b99-95b5-374a227a4e45',
      },
      {
        displayName: 'CSV - Donations',
        internalName: 'CSVDonations',
        listId: '008b1454-a5a4-4fff-837a-4c330310d799',
      },
      {
        displayName: 'CSV-Owners',
        internalName: 'CSVOwners',
        listId: '4f9291fc-6011-4fab-81ec-5a596ba98b6b',
      },
      {
        displayName: 'Donor Status',
        internalName: 'DonorStatus',
        listId: '2f3ff74e-be28-4fc6-802b-3880c1473915',
      },
      {
        displayName: 'Conversions per Quarter - Donors',
        internalName: 'ConversionsPerQuarterDonors',
        listId: '68b014e9-d79c-47c6-9211-223fee40570a',
      },
    ],
  },
  {
    title: 'Pipeline / Workflow Lists',
    icon: <NodeIndexOutlined />,
    lists: [
      {
        displayName: 'Pipeline Stage',
        internalName: 'PipelineStage',
        listId: '02e1f670-8a34-4356-b794-492237dee2d3',
      },
      {
        displayName: 'Pipeline-StageTracker',
        internalName: 'PipelineStageTracker',
        listId: '24cd7052-94f5-4360-9f6d-03a3ad78b705',
      },
    ],
  },
  {
    title: 'System / Integration Lists',
    icon: <SettingOutlined />,
    lists: [
      {
        displayName: 'DROPSUploadLog',
        internalName: 'DROPSUploadLog',
        listId: '3b6a018f-03e1-4c79-a993-306f4d45dc2b',
      },
      {
        displayName: 'UpdateDatabase-Trigger',
        internalName: 'UpdateDatabaseTrigger',
        listId: 'c4c1f37d-2923-4b1f-9f88-2f20624aca86',
      },
    ],
  },
  {
    title: 'Other Lists',
    icon: <AppstoreOutlined />,
    lists: [
      {
        displayName: 'Goals',
        internalName: 'Goals',
        listId: '7aec5b76-68a1-4199-9f2e-00c3e140c146',
      },
      {
        displayName: 'Partner Event Log',
        internalName: 'PartnerLog',
        listId: 'db222e92-d6c5-4a6e-aeee-13e71491e27a',
      },
      {
        displayName: 'Team',
        internalName: 'Team',
        listId: '99f75c03-732f-4fc1-be8a-d95143a6d45b',
      },
      {
        displayName: 'Offices Location',
        internalName: 'Offices Location',
        listId: '5f79ec0d-7ba5-489c-aa8b-bf7cf5530273',
      },
      {
        displayName: 'Mobile-Notes',
        internalName: 'MobileNotes',
        listId: '3b90d2db-a5dc-4d3e-9ac5-74716eccf925',
      },
      {
        displayName: 'NextContactCalendar',
        internalName: 'NextContactCalendar',
        listId: 'e11b5ea0-47ea-4103-a07d-56a025255802',
      },
      {
        displayName: 'Support-Feedback',
        internalName: 'SupportFeedback',
        listId: '201db96c-7954-49c2-9c7a-8754e9f05e49',
      },
      {
        displayName: 'SecurityGroups',
        internalName: 'SecurityGroups',
        listId: 'e4d94136-1b61-4c06-827a-1d47e3cab683',
      },
      {
        displayName: 'Security',
        internalName: 'Security',
        listId: 'e20fc58f-46b5-4dcf-bb8b-65a58416fafd',
      },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Sub-component: single list panel content                           */
/* ------------------------------------------------------------------ */

const ListPanel: FC<{
  list: IPawsList;
  onRowCountChange?: (listId: string, count: number) => void;
}> = ({ list, onRowCountChange }) => {
  const colDefs = LIST_COLUMNS[list.listId] ?? [];
  const [loadedItems, setLoadedItems] = useState<
    Record<string, unknown>[] | null
  >(null);
  const [pageSize, setPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);

  const isCompanyDetails = list.listId === COMPANY_DETAILS_LIST_ID;

  // Fetch DROPS locations for CompanyDetails matching
  const { data: locationsData } = useGetLocationsQuery({
    variables: { input: { take: 1000 } },
    skip: !isCompanyDetails,
  });

  // Build a case-insensitive name → location map
  const locationMap = useMemo(() => {
    const map = new Map<string, { name: string; id: string }>();
    if (!locationsData) return map;
    for (const loc of locationsData.getLocations.data) {
      map.set(loc.name.toLowerCase().trim(), { name: loc.name, id: loc.id });
    }
    return map;
  }, [locationsData]);

  // For CompanyDetails, enrich items with DROPS location data
  const enrichedItems = useMemo(() => {
    if (!loadedItems || !isCompanyDetails || locationMap.size === 0)
      return loadedItems;
    return loadedItems.map((item) => {
      const daycare = cellToString(item.Title).toLowerCase().trim();
      const match = locationMap.get(daycare);
      return {
        ...item,
        _dropsLocName: match?.name ?? '',
        _dropsLocId: match?.id ?? '',
      };
    });
  }, [loadedItems, isCompanyDetails, locationMap]);

  // Build columns with sort/filter based on loaded data (DROPS cols first)
  const allColDefs = useMemo(
    () =>
      isCompanyDetails
        ? [
            { name: '_dropsLocName', display: 'DROPS Loc Name' },
            { name: '_dropsLocId', display: 'DROPS Loc UUID' },
            ...colDefs,
          ]
        : colDefs,
    [colDefs, isCompanyDetails]
  );

  // Column visibility state — all visible by default
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(new Set());

  const toggleCol = useCallback((colName: string) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(colName)) next.delete(colName);
      else next.add(colName);
      return next;
    });
  }, []);

  const antColumns = useMemo(() => {
    const dataCols = buildAntCols(
      allColDefs,
      enrichedItems ?? loadedItems ?? []
    ).filter((c) => !hiddenCols.has(c.key as string));
    const rowNumCol: ColumnsType<Record<string, unknown>>[number] = {
      title: '#',
      key: '_rowNum',
      width: 52,
      className: styles.pawsRowNumCol,
      render: (
        _val: unknown,
        _record: Record<string, unknown>,
        index: number
      ) => (currentPage - 1) * pageSize + index + 1,
    };
    return [rowNumCol, ...dataCols];
  }, [
    allColDefs,
    enrichedItems,
    loadedItems,
    hiddenCols,
    currentPage,
    pageSize,
  ]);

  const [fetchItems, { loading }] = useLazyQuery(
    GET_SHAREPOINT_LIST_ITEMS_QUERY,
    {
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const result = data?.getSharepointListItems;
        if (result && result.sharepointEnabled === false) {
          message.error(
            'SharePoint integration not available — check backend Azure credentials'
          );
        }
        setLoadedItems(result?.items ?? []);
        onRowCountChange?.(list.listId, result?.totalCount ?? 0);
      },
    }
  );

  const handleLoad = useCallback(() => {
    fetchItems({ variables: { listId: list.listId } });
  }, [fetchItems, list.listId]);

  // Sync top scrollbar with table's horizontal scroll
  const topScrollRef = useRef<HTMLDivElement>(null);
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const syncing = useRef(false);

  useEffect(() => {
    const topEl = topScrollRef.current;
    const wrapEl = tableWrapRef.current;
    if (!topEl || !wrapEl) return;

    // Find the actual antd scroll container inside the table wrapper
    const tableBody = wrapEl.querySelector(
      '.ant-table-body'
    ) as HTMLElement | null;
    if (!tableBody) return;

    // Match the inner width so the top scrollbar has the same range
    const inner = topEl.firstElementChild as HTMLElement | null;
    if (inner) inner.style.width = `${tableBody.scrollWidth}px`;

    const onTopScroll = () => {
      if (syncing.current) return;
      syncing.current = true;
      tableBody.scrollLeft = topEl.scrollLeft;
      syncing.current = false;
    };
    const onTableScroll = () => {
      if (syncing.current) return;
      syncing.current = true;
      topEl.scrollLeft = tableBody.scrollLeft;
      syncing.current = false;
    };

    topEl.addEventListener('scroll', onTopScroll);
    tableBody.addEventListener('scroll', onTableScroll);
    return () => {
      topEl.removeEventListener('scroll', onTopScroll);
      tableBody.removeEventListener('scroll', onTableScroll);
    };
  }, [loadedItems]);

  const colPickerContent = (
    <div className={styles.pawsColPickerList}>
      {allColDefs.map((c) => (
        <div key={c.name} className={styles.pawsColPickerItem}>
          <Checkbox
            checked={!hiddenCols.has(c.name)}
            onChange={() => toggleCol(c.name)}
          >
            {c.display}
          </Checkbox>
        </div>
      ))}
    </div>
  );

  const popoverWrapRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <div
        ref={popoverWrapRef}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 6,
        }}
      >
        <span className={styles.pawsListId} style={{ marginBottom: 0 }}>
          List ID: <code>{list.listId}</code>
        </span>
        <Popover
          content={colPickerContent}
          title="Show / Hide Columns"
          trigger="click"
          placement="bottomRight"
          overlayClassName={styles.pawsColPickerPopover}
          getPopupContainer={() => popoverWrapRef.current || document.body}
        >
          <Button
            size="small"
            icon={<EyeOutlined />}
            className={styles.pawsColPickerBtn}
          >
            {allColDefs.length - hiddenCols.size} / {allColDefs.length} columns
          </Button>
        </Popover>
      </div>
      {/* Top scrollbar mirror */}
      <div ref={topScrollRef} className={styles.pawsTopScroll}>
        <div style={{ height: 1 }} />
      </div>
      <div className={styles.pawsTable} ref={tableWrapRef}>
        <Table
          columns={antColumns}
          dataSource={enrichedItems ?? loadedItems ?? []}
          rowKey={(_, idx) => String(idx)}
          size="small"
          pagination={
            loadedItems
              ? {
                  pageSize,
                  current: currentPage,
                  onChange: (page) => setCurrentPage(page),
                  onShowSizeChange: (_cur, size) => {
                    setPageSize(size);
                    setCurrentPage(1);
                  },
                  size: 'small',
                  showSizeChanger: true,
                  pageSizeOptions: ['50', '100', '500'],
                  showTotal: (total) => `${total} rows`,
                }
              : false
          }
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: loading ? (
              <span>
                <LoadingOutlined /> Loading...
              </span>
            ) : loadedItems ? (
              'List is empty'
            ) : (
              <Button
                type="primary"
                ghost
                size="small"
                icon={<CloudDownloadOutlined />}
                onClick={handleLoad}
              >
                Load Data from List
              </Button>
            ),
          }}
        />
      </div>
    </>
  );
};

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

const OpsPawsTab: FC = () => {
  const [rowCounts, setRowCounts] = useState<Record<string, number>>({});

  const handleRowCountChange = useCallback((listId: string, count: number) => {
    setRowCounts((prev) => ({ ...prev, [listId]: count }));
  }, []);

  return (
    <div className={`${styles.pawsTabWrap} ${styles.animFadeUp}`}>
      <div className={styles.pawsTabDescription}>
        Debug view of all SharePoint lists from the PAWS CRM site. Expand a list
        and click Load to fetch all rows (up to 15,000).
      </div>

      {SECTIONS.map((section) => (
        <div key={section.title}>
          <div className={styles.pawsSectionTitle}>
            {section.icon}
            {section.title}
          </div>
          <div className={styles.pawsCollapse}>
            <Collapse accordion>
              {section.lists.map((list) => {
                const cols = LIST_COLUMNS[list.listId] ?? [];
                const rowCount = rowCounts[list.listId];
                return (
                  <Panel
                    header={
                      <span>
                        <span className={styles.pawsListName}>
                          {list.displayName}
                        </span>
                        <span className={styles.pawsListInternal}>
                          {list.internalName}
                        </span>
                        <span className={styles.pawsListInternal}>
                          (
                          {cols.length +
                            (list.listId === COMPANY_DETAILS_LIST_ID
                              ? 2
                              : 0)}{' '}
                          columns
                          {rowCount !== undefined ? `, ${rowCount} rows` : ''})
                        </span>
                      </span>
                    }
                    key={list.listId}
                  >
                    <ListPanel
                      list={list}
                      onRowCountChange={handleRowCountChange}
                    />
                  </Panel>
                );
              })}
            </Collapse>
          </div>
        </div>
      ))}
    </div>
  );
};

export default OpsPawsTab;
